import React, { useEffect, useRef, useState } from 'react'
import styles from './Labels.module.css'
import { plural, type LabelPerson } from './model'

const NO_GROUP = '-'
const nameOf = (p: LabelPerson) => p.name || 'Unnamed person'

// The header box takes every person the search and groups show, clears them
// when they are all taken, and shows a dash when only some are (h 4686).
function MatchingBox({ all, some, disabled, onChange }: { all: boolean; some: boolean; disabled: boolean; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some && !all
  }, [some, all])
  return (
    <input
      ref={ref}
      type="checkbox"
      className={styles.pickBox}
      aria-label="Select every matching person"
      checked={all}
      disabled={disabled}
      onChange={onChange}
    />
  )
}

// Who gets a label (§7m.3 stage 2, h 4648-4650 and 4686-4689): search active
// people by name, narrow to one or more groups, one label each. Only active
// people are ever listed, so Select all cannot take an inactive one. The
// selection survives a new search or group, so a batch can be put together
// from several; the footer counts it all and says how much is out of view.
export function PeoplePicker({
  people,
  failed,
  selected,
  onChange,
}: {
  people: LabelPerson[] | null
  failed: string | null
  selected: Set<string>
  onChange: (next: Set<string>) => void
}) {
  const [q, setQ] = useState('')
  const [groups, setGroups] = useState<string[]>([])

  if (failed)
    return (
      <div className={styles.picker}>
        <p className={styles.pickerNote} role="alert">
          {failed}
        </p>
      </div>
    )
  if (!people)
    return (
      <div className={styles.picker}>
        <p className={styles.pickerNote}>Loading people…</p>
      </div>
    )
  if (!people.length)
    return (
      <div className={styles.picker}>
        <p className={styles.pickerNote}>Nobody is active yet. People are added in Personnel.</p>
      </div>
    )

  const named = Array.from(new Map(people.filter((p) => p.group_id).map((p) => [p.group_id as string, p.group_name || 'Unnamed group'])).entries()).sort((a, b) =>
    a[1].localeCompare(b[1])
  )
  const chips = people.some((p) => !p.group_id) ? [...named, [NO_GROUP, 'No group'] as [string, string]] : named
  const needle = q.trim().toLowerCase()
  const inGroups = (p: LabelPerson) => !groups.length || groups.includes(p.group_id || NO_GROUP)
  const matching = people.filter((p) => (!needle || nameOf(p).toLowerCase().includes(needle)) && inGroups(p))
  const taken = matching.filter((p) => selected.has(p.id)).length
  const all = matching.length > 0 && taken === matching.length
  const total = people.filter((p) => selected.has(p.id)).length
  const outOfView = total - taken

  const flip = (id: string) => {
    const n = new Set(selected)
    if (n.has(id)) n.delete(id)
    else n.add(id)
    onChange(n)
  }
  const flipMatching = () => {
    const n = new Set(selected)
    for (const p of matching) {
      if (all) n.delete(p.id)
      else n.add(p.id)
    }
    onChange(n)
  }
  const flipGroup = (id: string) => setGroups((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]))

  return (
    <div className={styles.picker}>
      <div className={styles.pickerTools}>
        <MatchingBox all={all} some={taken > 0} disabled={!matching.length} onChange={flipMatching} />
        <svg className={styles.pickerSearchIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          className={styles.pickerInput}
          type="search"
          placeholder="Search active people by name"
          aria-label="Search people by name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <span className={styles.pickerMatch}>
          {matching.length.toLocaleString('en-NZ')} of {people.length.toLocaleString('en-NZ')} active
        </span>
      </div>
      {chips.length > 0 && (
        <div className={styles.pickerChips} role="group" aria-label="Groups">
          {chips.map(([id, name]) => (
            <button
              key={id}
              type="button"
              aria-pressed={groups.includes(id)}
              className={[styles.pickerChip, groups.includes(id) ? styles.pickerChipOn : ''].join(' ')}
              onClick={() => flipGroup(id)}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      <div className={styles.pickerList} role="group" aria-label="People">
        {matching.length === 0 ? (
          <p className={styles.pickerNote}>Nobody matches that search.</p>
        ) : (
          matching.map((p) => (
            <label key={p.id} className={[styles.pickerRow, selected.has(p.id) ? styles.pickerRowOn : ''].join(' ')}>
              <input type="checkbox" className={styles.pickBox} checked={selected.has(p.id)} onChange={() => flip(p.id)} />
              <span className={styles.pickerText}>
                <span className={styles.pickerName}>{nameOf(p)}</span>
                <span className={styles.pickerGroupName}>{p.group_name || 'No group'}</span>
              </span>
            </label>
          ))
        )}
      </div>
      <div className={styles.pickerFoot}>
        <span>Inactive people never appear here and can't be included.</span>
        <span className={styles.pickerCount} data-testid="picker-count">
          {total ? `${plural(total, 'person', 'people')} selected` : 'Nobody selected'}
          {outOfView > 0 && ` · ${outOfView.toLocaleString('en-NZ')} not shown by this search`}
        </span>
      </div>
    </div>
  )
}
